import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { Card, Overline, StatCard, ProgressBar, Btn, Pill, Icon, PageLoader, SectionTitle } from "../../components/ui-kit";
import { cn } from "../../lib/utils";

const heatColor = (m) => m >= 75 ? "#20B982" : m >= 55 ? "#F3B83F" : "#EF6B6B";

export default function TeacherDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/teacher/dashboard").then(({ data }) => setD(data)); }, []);
  if (!d) return <PageLoader />;

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-6">
      <div className="relative rounded-[28px] p-6 sm:p-7 text-white overflow-hidden" style={{ background: "linear-gradient(135deg,#2f86f5,#1b5fc0)" }}>
        <div className="absolute inset-0 dotgrid opacity-50" />
        <div className="relative flex items-center justify-between flex-wrap gap-4">
          <div><Overline className="text-white/70">Teacher Workspace</Overline><h1 className="text-3xl sm:text-4xl font-bold mt-1 tracking-tight">Welcome, {d.name.split(" ")[0]}</h1></div>
          <div className="flex gap-2">
            <Link to="/teacher/studio"><Btn variant="glass" icon="Sparkles">AI Studio</Btn></Link>
            <Link to="/teacher/curriculum"><Btn variant="white" icon="Wand2">Generate Curriculum</Btn></Link>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon="Users" label="Students" value={d.total_students} tone="blue" sub={`${d.active_students} active`} />
        <StatCard icon="Target" label="Quiz Average" value={`${d.quiz_average}%`} tone="green" />
        <StatCard icon="CalendarCheck" label="Attendance" value={`${d.attendance}%`} tone="cyan" />
        <StatCard icon="TriangleAlert" label="Need Attention" value={d.at_risk.length} tone="coral" />
      </div>

      {/* Alerts */}
      <div>
        <SectionTitle title="Intervention Alerts" icon="Bell" />
        <div className="grid md:grid-cols-3 gap-4">
          {d.alerts.map((a) => (
            <Card key={a.id} className={cn("border-l-4", a.type === "risk" ? "border-l-mim-coral" : a.type === "positive" ? "border-l-mim-green" : "border-l-mim-yellow")}>
              <Icon name={a.type === "risk" ? "TriangleAlert" : a.type === "positive" ? "TrendingUp" : "Info"}
                className={cn("w-5 h-5 mb-2", a.type === "risk" ? "text-mim-coral" : a.type === "positive" ? "text-mim-green" : "text-[#b07d13]")} />
              <p className="text-sm font-medium text-mim-navy">{a.text}</p>
              <Link to="/teacher/students" className="text-xs font-semibold text-mim-blue hover:underline mt-2 inline-block">View students →</Link>
            </Card>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Topic mastery heatmap */}
        <Card>
          <SectionTitle title="Topic Mastery (Class 10)" icon="Grid3x3" />
          <div className="space-y-2.5">
            {d.heatmap.map((h) => (
              <div key={h.topic} className="flex items-center gap-3">
                <span className="text-sm text-mim-navy w-40 truncate">{h.topic}</span>
                <div className="flex-1"><ProgressBar value={h.mastery} color={heatColor(h.mastery)} /></div>
                <span className="text-xs font-semibold w-9 text-right" style={{ color: heatColor(h.mastery) }}>{h.mastery}%</span>
              </div>
            ))}
          </div>
        </Card>

        {/* At risk students */}
        <Card>
          <SectionTitle title="Students Needing Support" icon="LifeBuoy" />
          {d.at_risk.length === 0 ? <p className="text-sm text-mim-muted">All students are on track 🎉</p> : (
            <div className="space-y-3">
              {d.at_risk.map((s) => (
                <Link key={s.id} to={`/teacher/student/${s.id}`}>
                  <div className="flex items-center gap-3 p-3 rounded-2xl hover:bg-mim-soft/50 transition-colors">
                    <div className="w-9 h-9 rounded-full bg-mim-coral/10 text-mim-coral flex items-center justify-center text-sm font-bold">{s.name.split(" ").map(n=>n[0]).join("")}</div>
                    <div className="flex-1"><div className="font-semibold text-mim-navy text-sm">{s.name}</div><div className="text-xs text-mim-muted">{s.reason}</div></div>
                    <Pill tone="coral">{s.mastery}%</Pill>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
